import * as React from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

import { Form } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Banner from 'components/banners/banner/Banner';

import DeleteUserTrigger from './components/trigger/DeleteUserTrigger';
import DeleteUserBannerPrimaryInfluencer from './influencer/banner/primary/DeleteUserBannerPrimaryInfluencer';
import DeleteUserReasons from './components/reasons/DeleteUserReasons';
import DeleteUserPassword from './components/password/DeleteUserPassword';

import { logout } from 'modules/access/reducers/session';
import { deleteUser } from './redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { redirectToIndexPage } from 'utils/routes';

import './delete-user.scss';

applyTranslations(require('./delete-user.i18n.json'));

import type { Account, OrganizationType } from 'types';

interface Props {
  account: Account,
  orgType: OrganizationType,
  profileId: number | null
}

const MSG_KEY = 'delete_user';

const DeleteUser: React.FC<Props> = ({
  account,
  orgType,
  profileId
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [selectedReasonCode, setSelectedReasonCode] = React.useState(null as string | null);
  const [otherReason, setOtherReason] = React.useState('');
  const [password, setPassword] = React.useState('');
  const dispatch = useDispatch();

  const handleToggle = (): void => {
    setIsExpanded(!isExpanded);
  };

  const handleReasonChange = (
    identifier: string
  ): void => {
    setSelectedReasonCode(identifier);
  };

  const handleReasonTextChange = (
    text: string
  ): void => {
    setOtherReason(text);
  };

  const handlePasswordChange = (
    value: string
  ): void => {
    setPassword(value);
  };

  const handleSubmit = async (): Promise<void> => {
    const result = await dispatch(deleteUser(
      account.id,
      password,
      selectedReasonCode,
      selectedReasonCode === 'other' ? otherReason : undefined
    ));

    // @ts-ignore for some reason, the return type for dispatch above is function
    if (result) {
      await dispatch(logout());
      redirectToIndexPage();
    }
  };

  const renderPrimaryBannerInfluencer = (): React.ReactNode => (
    <DeleteUserBannerPrimaryInfluencer />
  );

  const renderPrimaryBanner = (): React.ReactNode => (
    <div className="delete-user__banner--primary">
      <Banner
        background="on-white"
        withBorder
      >
        {orgType === 'influencer' && renderPrimaryBannerInfluencer()}
      </Banner>
    </div>
  );

  const renderReasons = (): React.ReactNode => (
    <DeleteUserReasons
      orgType={orgType}
      profileId={profileId}
      selectedOption={selectedReasonCode}
      otherText={otherReason}
      onChange={handleReasonChange}
      onTextChange={handleReasonTextChange}
    />
  );

  const renderPasswordField = (): React.ReactNode => (
    <DeleteUserPassword
      password={password}
      onChange={handlePasswordChange}
    />
  );

  const renderButton = (): React.ReactNode => (
    <ActionButton
      type="submit"
      color="primary"
      className="delete-user__button"
    >
      {__t(`${MSG_KEY}.buttons.delete_account`)}
    </ActionButton>
  );

  const contentClasses = classnames('delete-user__content', {
    'delete-user__content--expanded': isExpanded
  });

  return (
    <div className="delete-user">
      <Form
        className="delete-user__form"
        onSubmit={handleSubmit}
      >
        <DeleteUserTrigger
          onClick={handleToggle}
        />
        <div className={contentClasses}>
          {renderPrimaryBanner()}
          {renderReasons()}
          {renderPasswordField()}
          {renderButton()}
        </div>
      </Form>
    </div>
  );
};

export default DeleteUser;
